import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { Home, Loader2, ShieldCheck } from "lucide-react";

/**
 * iter260 — Book several properties in ONE Stripe checkout.
 * Each selected property keeps its own service, frequency, date and
 * price; the backend creates one booking + one recurring schedule per
 * property after payment.
 */
const FREQS = [
  { key: "weekly", label: "Weekly" },
  { key: "biweekly", label: "Every 2 weeks" },
  { key: "monthly", label: "Monthly" },
  { key: "one_time", label: "One-time" },
];
const tomorrowPlus = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const selectCls = "h-9 w-full px-2 rounded-lg border-2 border-gray-200 bg-white text-[12px] font-bold text-gray-900 focus:outline-none focus:border-emerald-500";

export default function MultiPropertyBookingDialog({ open, onOpenChange, properties }) {
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState({});
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/services").then(({ data }) => setServices(data?.services || [])).catch(() => setServices([]));
    const init = {};
    for (const p of properties) {
      init[p.property_id] = { checked: false, service_key: "lawn_mowing", frequency: "biweekly", preferred_date: tomorrowPlus(2) };
    }
    setRows(init);
    setQuote(null);
  }, [open, properties]);

  const items = useMemo(() => Object.entries(rows)
    .filter(([, r]) => r.checked)
    .map(([property_id, r]) => ({ property_id, service_key: r.service_key, frequency: r.frequency, preferred_date: r.preferred_date })),
  [rows]);

  useEffect(() => {
    if (!open || items.length === 0) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/me/multi-property/quote", { items });
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) { setQuote(null); toast.error(e?.response?.data?.detail || "Could not price these properties"); }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [items, open]);

  const priceFor = (pid) => quote?.items?.find((i) => i.property_id === pid)?.price;
  const update = (pid, patch) => setRows((s) => ({ ...s, [pid]: { ...s[pid], ...patch } }));

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/multi-property/checkout", { items, origin_url: window.location.origin });
      sessionStorage.setItem("last_booking", JSON.stringify({
        booking_id: data.group_id, price: data.total,
        service_name: `${data.items.length} properties booked`,
        preferred_date: data.items.map((i) => `${i.property_label || "Property"}: ${i.preferred_date}`).join(" · "),
        address: data.items.map((i) => i.property_label || i.address).join(", "), area: "",
      }));
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden" data-testid="multi-property-dialog">
        <DialogHeader className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-5 py-4">
          <DialogTitle className="text-white font-[Outfit] tracking-tight">Book multiple properties</DialogTitle>
          <DialogDescription className="text-emerald-100/80">
            One checkout. Each property keeps its own service, schedule and price.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-2.5">
          {properties.map((p) => {
            const r = rows[p.property_id];
            if (!r) return null;
            const price = priceFor(p.property_id);
            return (
              <div key={p.property_id}
                   className={`rounded-2xl border-2 p-3 transition-colors ${r.checked ? "border-emerald-500 bg-emerald-50/40" : "border-gray-200"}`}
                   data-testid={`multi-row-${p.property_id}`}>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox checked={r.checked} onCheckedChange={(v) => update(p.property_id, { checked: !!v })}
                            className="mt-0.5" data-testid={`multi-select-${p.property_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[13px] font-extrabold text-gray-900">
                      <Home className="w-3.5 h-3.5 text-[#047857]" /> {p.label || "Property"}
                    </div>
                    <div className="text-[11px] text-gray-600 truncate">{p.address} · {(p.square_feet || 0).toLocaleString()} sq ft</div>
                  </div>
                  {r.checked && (
                    <div className="text-[14px] font-extrabold tabular-nums text-emerald-800" data-testid={`multi-price-${p.property_id}`}>
                      {quoting && price === undefined ? <Loader2 className="w-4 h-4 animate-spin" /> : price !== undefined ? `$${price.toFixed(2)}` : "—"}
                    </div>
                  )}
                </label>
                {r.checked && (
                  <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select value={r.service_key} onChange={(e) => update(p.property_id, { service_key: e.target.value })}
                            className={selectCls} data-testid={`multi-service-${p.property_id}`}>
                      {(services.length ? services : [{ key: "lawn_mowing", name: "Lawn Mowing" }]).map((s) => (
                        <option key={s.key} value={s.key}>{s.name}</option>
                      ))}
                    </select>
                    <select value={r.frequency} onChange={(e) => update(p.property_id, { frequency: e.target.value })}
                            className={selectCls} data-testid={`multi-frequency-${p.property_id}`}>
                      {FREQS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                    <input type="date" min={tomorrowPlus(1)} value={r.preferred_date}
                           onChange={(e) => update(p.property_id, { preferred_date: e.target.value })}
                           className={selectCls} data-testid={`multi-date-${p.property_id}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="px-5 py-4 border-t border-gray-100 bg-gray-50/60 flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="text-[11px] text-gray-600 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
            Card saved securely · separate booking per property
          </div>
          <Button onClick={checkout} disabled={busy || quoting || !quote || items.length === 0}
                  className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11 px-6"
                  data-testid="multi-checkout-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            {items.length === 0 ? "Select properties" : `Pay $${(quote?.total || 0).toFixed(2)} · ${items.length} ${items.length === 1 ? "property" : "properties"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
